import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, API, formatApiErrorDetail, scaricaFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { euro, num, dateIt, STATI, TRASFERTE_TIPI } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { DocumentiCommessa } from "@/components/DocumentiCommessa";
import { DiarioCommessa } from "@/components/DiarioCommessa";
import { VeicoliCommessa, VEICOLO_TIPI } from "@/components/VeicoliCommessa";
import { AcquistiForm, voceVuota } from "@/components/AcquistiForm";
import { ModificaTestataFattura } from "@/components/ModificaTestataFattura";
import { FattureEmesse } from "@/components/FattureEmesse";
import { ConfermaEliminaCommessa } from "@/components/ConfermaEliminaCommessa";
import { ModificaOra } from "@/components/ModificaOra";
import { ModificaFattura, ModificaTrasferta } from "@/components/ModificaCosti";
import { ModificaOreBulk, ModificaTrasferteBulk, ModificaAcquistiBulk } from "@/components/ModificaBulkRighe";
import { SelettoreGiorni, ConfermaMultiGiorno, verificaLimitiOre } from "@/components/SelettoreGiorni";
import { SelettoreDipendenti } from "@/components/SelettoreDipendenti";
import { ModificaCommessa } from "@/components/ModificaPreventivo";
import { IntestazioneOre, INTESTAZIONE_ORE_INIZIALE, giorniEffettivi, nottiDaGiorni } from "@/components/IntestazioneOre";
import { PannelloDipendentiGiorno } from "@/components/PannelloDipendentiGiorno";
import { MaterialiCommessa } from "@/components/magazzino/MaterialiCommessa";
import { CaricoDialog, caricoInForm } from "@/components/magazzino/CaricoDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { RiepilogoOre } from "@/components/RiepilogoOre";
import { RiepilogoPrestazioni } from "@/components/prestazioni/RiepilogoPrestazioni";
import { ConfrontoPreventivo } from "@/components/preventivi/ConfrontoPreventivo";
import { TIPO_ORA_LABEL } from "@/lib/tipiOra";
import { HardHat, Lock } from "lucide-react";
import {
  Pencil,
  ArrowLeft,
  FileDown,
  FileSpreadsheet,
  Trash2,
  Plus,
  AlertTriangle,
  Clock,
  Package,
  Car,
  BedDouble,
  FileText,
  Mail,
  UtensilsCrossed,
  Weight,
} from "lucide-react";

const CompareRow = ({ label, prev, real, testid }) => {
  const over = real > prev;
  const pct = prev > 0 ? Math.min((real / prev) * 100, 100) : real > 0 ? 100 : 0;
  const scostamento = prev > 0 ? ((real - prev) / prev) * 100 : real > 0 ? Infinity : 0;
  const sforamento = scostamento > 10;
  return (
    <div className="py-3" data-testid={testid}>
      <div className="flex items-center justify-between gap-2 text-sm mb-1.5">
        <span className="font-medium text-slate-700 flex items-center gap-2 min-w-0">
          <span className="truncate">{label}</span>
          {sforamento && (
            <span className="inline-flex items-center gap-1 rounded-full bg-red-100 text-red-700 border border-red-200 px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap" title="Il costo reale supera il preventivo di oltre il 10%" data-testid={testid ? `${testid}-sforamento` : undefined}>
              <AlertTriangle className="h-3 w-3" />
              {Number.isFinite(scostamento) ? `+${scostamento.toFixed(1).replace(".", ",")}%` : "Non previsto"}
            </span>
          )}
        </span>
        <span className="font-mono text-xs text-slate-500 whitespace-nowrap">
          Prev. {euro(prev)} · Reale{" "}
          <span className={over ? "text-red-600 font-semibold" : "text-emerald-600 font-semibold"}>
            {euro(real)}
          </span>
        </span>
      </div>
      <Progress
        value={pct}
        className="h-2"
        indicatorClassName={over ? "bg-red-500" : "bg-emerald-500"}
      />
    </div>
  );
};

export default function CommessaDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { isAdmin } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ["commessa-dash", id],
    queryFn: async () => (await api.get(`/commesse/${id}/dashboard`)).data,
  });
  const [filtroDip, setFiltroDip] = useState("tutti");
  const [editPrev, setEditPrev] = useState(false);
  const [oreDal, setOreDal] = useState("");
  const [oreAl, setOreAl] = useState("");
  const [traDip, setTraDip] = useState("tutti");
  const [traDal, setTraDal] = useState("");
  const [traAl, setTraAl] = useState("");
  const [traSoloDup, setTraSoloDup] = useState(false);
  const [rimbSoloDup, setRimbSoloDup] = useState(false);
  const [filtroForn, setFiltroForn] = useState("tutti");
  const [filtroMat, setFiltroMat] = useState("tutti");
  const [filtroPag, setFiltroPag] = useState("tutti");
  const [filtroDal, setFiltroDal] = useState("");
  const [filtroAl, setFiltroAl] = useState("");
  const { data: ore = [] } = useQuery({
    queryKey: ["ore", id],
    queryFn: async () => (await api.get(`/commesse/${id}/ore`)).data,
  });
  const oreFiltrate = ore
    .filter((o) => filtroDip === "tutti" || o.dipendente_id === filtroDip)
    .filter((o) => !oreDal || (o.data || "").slice(0, 10) >= oreDal)
    .filter((o) => !oreAl || (o.data || "").slice(0, 10) <= oreAl);
  const filtriOreAttivi = filtroDip !== "tutti" || !!oreDal || !!oreAl;
  const filtriMatAttivi = filtroForn !== "tutti" || filtroMat !== "tutti" || filtroPag !== "tutti" || !!filtroDal || !!filtroAl;

  const { data: materiali = [] } = useQuery({
    queryKey: ["materiali", id],
    queryFn: async () => (await api.get(`/commesse/${id}/materiali`)).data,
  });
  const materialiFiltrati = materiali
    .filter((m) => filtroForn === "tutti" || (m.fornitore_id || "__nessuno__") === filtroForn)
    .filter((m) => filtroMat === "tutti" || (m.descrizione || "").trim().toLowerCase() === filtroMat)
    .filter((m) => filtroPag === "tutti" || (filtroPag === "pagate" ? !!m.data_pagamento : !m.data_pagamento))
    .filter((m) => !filtroDal || (m.data || "").slice(0, 10) >= filtroDal)
    .filter((m) => !filtroAl || (m.data || "").slice(0, 10) <= filtroAl)
    .slice()
    .sort((a, b) => (b.data || "").slice(0, 10).localeCompare((a.data || "").slice(0, 10)) || (a.fornitore_id || "").localeCompare(b.fornitore_id || "") || (a.numero_fattura || "").localeCompare(b.numero_fattura || ""));
  const chiaveFattura = (m) => `${m.fornitore_id || ""}|${(m.numero_fattura || "").trim().toLowerCase()}|${(m.data || "").slice(0, 10)}`;

  const { data: trasferte = [] } = useQuery({
    queryKey: ["trasferte", id],
    queryFn: async () => (await api.get(`/commesse/${id}/trasferte`)).data,
  });
  const chiaveTra = (t) => `${t.dipendente_id}|${(t.data || "").slice(0, 10)}`;
  const contaTra = trasferte.reduce((m, t) => { const k = chiaveTra(t); m[k] = (m[k] || 0) + 1; return m; }, {});
  const traDupIds = new Set(trasferte.filter((t) => contaTra[chiaveTra(t)] > 1).map((t) => t.id));
  const chiaveRimb = (r) => `${r.dipendente_id}|${r.tipo}|${(r.data || "").slice(0, 10)}`;
  const trasferteFiltrate = trasferte
    .filter((t) => traDip === "tutti" || t.dipendente_id === traDip)
    .filter((t) => !traDal || (t.data || "").slice(0, 10) >= traDal)
    .filter((t) => !traAl || (t.data || "").slice(0, 10) <= traAl)
    .filter((t) => !traSoloDup || traDupIds.has(t.id))
    .map((t) => (traDupIds.has(t.id) ? { ...t, _dup: true } : t))
    .sort((a, b) => (traSoloDup ? chiaveTra(a).localeCompare(chiaveTra(b)) || (a.data || "").localeCompare(b.data || "") : 0));
  const filtriTraAttivi = traDip !== "tutti" || !!traDal || !!traAl || traSoloDup;
  const { data: dipendenti = [] } = useQuery({
    queryKey: ["dipendenti"],
    queryFn: async () => (await api.get("/dipendenti")).data,
  });
  const [oreForm, setOreForm] = useState({ ...INTESTAZIONE_ORE_INIZIALE(), ore: "", ore_straordinarie: "" });
  const [caricoOpen, setCaricoOpen] = useState(false);
  const [matForm, setMatForm] = useState({
    fornitore_id: "",
    numero_fattura: "",
    data: new Date().toISOString().slice(0, 10),
    voci: [voceVuota()],
  });
  const [traForm, setTraForm] = useState({ dipendenti_ids: [], indennita_notte: "", descrizione: "", data: new Date().toISOString().slice(0, 10) });
  const [traGiorni, setTraGiorni] = useState([]);
  const [pendingTra, setPendingTra] = useState(null);
  const { data: impostazioni } = useQuery({
    queryKey: ["impostazioni"],
    queryFn: async () => (await api.get("/impostazioni")).data,
  });
  const { data: rimborsi = [] } = useQuery({
    queryKey: ["rimborsi", id],
    queryFn: async () => (await api.get(`/commesse/${id}/rimborsi`)).data,
  });
  const contaRimb = rimborsi.reduce((m, r) => { const k = chiaveRimb(r); m[k] = (m[k] || 0) + 1; return m; }, {});
  const rimbDupIds = new Set(rimborsi.filter((r) => contaRimb[chiaveRimb(r)] > 1).map((r) => r.id));
  const rimbDelMut = useMutation({
    mutationFn: async (rid) => (await api.delete(`/rimborsi/${rid}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rimborsi", id] });
      qc.invalidateQueries({ queryKey: ["commessa-dash", id] });
      toast.success("Rimborso eliminato");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const { data: veicoliCosti = [] } = useQuery({
    queryKey: ["veicoli-costi", id],
    queryFn: async () => (await api.get(`/commesse/${id}/veicoli-costi`)).data,
  });

  const { data: prestTot } = useQuery({
    queryKey: ["prest-riepilogo", id, "tutto"],
    queryFn: async () => (await api.get(`/prestazioni/${id}/riepilogo`)).data,
    enabled: data?.commessa?.tipo === "prestazione",
  });

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ["commessa-dash", id] });
    qc.invalidateQueries({ queryKey: ["rimborsi", id] });
    qc.invalidateQueries({ queryKey: ["commesse"] });
    qc.invalidateQueries({ queryKey: ["dashboard-generale"] });
    qc.invalidateQueries({ queryKey: ["stato-giorno"] });
    qc.invalidateQueries({ queryKey: ["prest-riepilogo"] });
    qc.invalidateQueries({ queryKey: ["prest-dashboard"] });
  };

  const [oraInModifica, setOraInModifica] = useState(null);
  const [oreBulk, setOreBulk] = useState(null);
  const [traBulk, setTraBulk] = useState(null);
  const [matBulk, setMatBulk] = useState(null);
  const [fatturaInModifica, setFatturaInModifica] = useState(null);
  const [caricoInModifica, setCaricoInModifica] = useState(null);
  const apriModificaAcquisto = async (r) => {
    if (!r.mag_carico_id) return setFatturaInModifica(r);
    try { const c = (await api.get(`/magazzino/carichi/${r.mag_carico_id}`)).data; setCaricoInModifica({ editId: c.id, iniziale: caricoInForm(c) }); }
    catch { toast.error("Documento di magazzino non trovato"); }
  };
  const [testataInModifica, setTestataInModifica] = useState(null);
  const pagFatturaMut = useMutation({
    mutationFn: async ({ ids, data_pagamento }) => (await api.put("/materiali/fattura/pagamento", { ids, data_pagamento })).data,
    onSuccess: (r, v) => {
      qc.invalidateQueries({ queryKey: ["materiali", id] });
      toast.success(v.data_pagamento ? `Fattura segnata pagata (${r.aggiornate} voci)` : "Pagamento annullato");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const [trasfertaInModifica, setTrasfertaInModifica] = useState(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const [bloccoFatture, setBloccoFatture] = useState(null);
  const delCommessaMut = useMutation({
    mutationFn: async (force) =>
      (await api.delete(`/commesse/${id}`, { params: force ? { force: true } : {} })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["commesse"] });
      toast.success(data?.commessa?.tipo === "prestazione" ? "Prestazione spostata nel cestino" : "Commessa spostata nel cestino");
      navigate(data?.commessa?.tipo === "prestazione" ? "/prestazioni" : "/commesse");
    },
    onError: (e) => {
      if (e.response?.status === 409) setBloccoFatture(formatApiErrorDetail(e.response.data.detail));
      else toast.error(formatApiErrorDetail(e.response?.data?.detail));
    },
  });

  const statoMut = useMutation({
    mutationFn: async (stato) =>
      (await api.patch(`/commesse/${id}/stato`, { stato })).data,
    onSuccess: () => {
      invalidateAll();
      toast.success("Stato aggiornato");
    },
  });

  const [pendingOre, setPendingOre] = useState(null);
  const oreMut = useMutation({
    mutationFn: async ({ giorni, dipendenti_ids, avvisi, trasferta, cena_trasferta, ...p }) => {
      const ordinati = [...giorni].sort();
      for (const dipendente_id of dipendenti_ids) for (const [i, data] of ordinati.entries()) {
        const nonUltimo = i < ordinati.length - 1;
        await api.post(`/commesse/${id}/ore`, { ...p, dipendente_id, data, trasferta_notte: !!trasferta && nonUltimo, cena_trasferta: !!cena_trasferta && nonUltimo });
      }
      return giorni.length * dipendenti_ids.length;
    },
    onSuccess: (n) => {
      qc.invalidateQueries({ queryKey: ["ore", id] });
      qc.invalidateQueries({ queryKey: ["trasferte", id] });
      qc.invalidateQueries({ queryKey: ["rimborsi", id] });
      invalidateAll();
      setOreForm((f) => ({ ...f, ore: "", ore_straordinarie: "", note: "", giorni: [], trasferta: false, cena_trasferta: false }));
      setPendingOre(null);
      toast.success(n > 1 ? `${n} righe ore registrate` : "Ore registrate");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const matMut = useMutation({
    mutationFn: async (payloads) => {
      for (const p of payloads) await api.post(`/commesse/${id}/materiali`, p);
      return payloads.length;
    },
    onSuccess: (n) => {
      qc.invalidateQueries({ queryKey: ["materiali", id] });
      invalidateAll();
      setMatForm((f) => ({ ...f, numero_fattura: "", voci: [voceVuota()] }));
      qc.invalidateQueries({ queryKey: ["materiali-descrizioni"] });
      toast.success(n > 1 ? `Fattura registrata con ${n} voci` : "Acquisto registrato");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const pagMut = useMutation({
    mutationFn: async ({ id, data_pagamento }) => (await api.put(`/materiali/${id}/pagamento`, { data_pagamento })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["materiali", id] });
      toast.success("Pagamento aggiornato");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const traMut = useMutation({
    mutationFn: async (p) => (await api.post(`/commesse/${id}/trasferte/bulk`, p)).data,
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["trasferte", id] });
      qc.invalidateQueries({ queryKey: ["rimborsi", id] });
      invalidateAll();
      setTraForm((f) => ({ ...f, descrizione: "" }));
      setTraGiorni([]);
      setPendingTra(null);
      toast.success(`${r.creati} ${r.creati === 1 ? "notte registrata" : "notti registrate"}`);
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const delMut = useMutation({
    mutationFn: async ({ kind, itemId }) => (await api.delete(`/${kind}/${itemId}`)).data,
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: [v.qkey, id] });
      invalidateAll();
      toast.success("Voce eliminata");
    },
  });
  const eliminaMolte = async (kind, qkey, righe) => {
    try {
      for (const r of righe) await api.delete(`/${kind}/${r.id}`);
      toast.success(`${righe.length} righe eliminate`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      qc.invalidateQueries({ queryKey: [qkey, id] });
      if (kind === "ore") { qc.invalidateQueries({ queryKey: ["rimborsi", id] }); qc.invalidateQueries({ queryKey: ["trasferte", id] }); }
      if (kind === "trasferte") qc.invalidateQueries({ queryKey: ["rimborsi", id] });
      invalidateAll();
    }
  };

  if (isLoading || !data)
    return <div className="text-slate-400 py-20 text-center">Caricamento commessa…</div>;

  const c = data.commessa;
  const prev = data.preventivo;
  const isPrest = c.tipo === "prestazione";
  const inPerdita = !isPrest && data.in_perdita;
  const prestFatt = prestTot?.totali?.totale_fatturabile || 0;
  const prestMargine = Math.round((prestFatt - data.costi_reali_totali) * 100) / 100;

  return (
    <div className="space-y-6">
      <button
        onClick={() => navigate(isPrest ? "/prestazioni" : "/commesse")}
        className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800"
        data-testid="back-button"
      >
        <ArrowLeft className="h-4 w-4" /> {isPrest ? "Torna alle prestazioni" : "Torna alle commesse"}
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <span className="font-mono text-sm text-slate-500">{c.codice}</span>
            {c.preventivo_origine?.numero && <button onClick={() => navigate(`/preventivi/${c.preventivo_origine.preventivo_id}`)} className="text-xs rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 hover:bg-emerald-200" title="Apri il preventivo di origine" data-testid="commessa-da-preventivo">da preventivo {c.preventivo_origine.numero}</button>}
            {!isPrest && null}            {isPrest && <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border bg-orange-50 text-primary border-orange-200" data-testid="prest-badge"><HardHat className="h-3 w-3" /> Prestazione manodopera</span>}
            <span
              className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium border ${STATI[c.stato]?.cls}`}
            >
              {STATI[c.stato]?.label}
            </span>
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mt-1">
            {isPrest ? c.cliente_nome : c.descrizione}
            {c.categoria_lavoro && <span className="ml-3 align-middle text-[11px] font-sans font-medium uppercase tracking-wider rounded-full bg-slate-100 text-slate-600 px-2 py-0.5" data-testid="commessa-categoria-lavoro">{c.categoria_lavoro}</span>}
          </h1>
          <p className="text-slate-500">
            {isPrest ? <>{c.descrizione} · Apertura {dateIt(c.data_apertura)}{prestTot?.tariffa_corrente && <> · Tariffa <b className="font-mono text-slate-700">{euro(prestTot.tariffa_corrente.tariffa_giornaliera)}</b>/persona/giorno · rincaro materiale <b className="font-mono text-slate-700">{prestTot.tariffa_corrente.rincaro_materiale_perc}%</b></>}</> : <>{c.cliente_nome} · Apertura {dateIt(c.data_apertura)}</>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {true && (
            <Select value={c.stato} onValueChange={(v) => statoMut.mutate(v)}>
              <SelectTrigger className="w-[150px] bg-white" data-testid="cambia-stato-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(STATI).map(([k, v]) => (
                  <SelectItem key={k} value={k}>
                    {v.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {true && (
            <Button
              variant="outline"
              onClick={() => scaricaFile(`${API}/commesse/${id}/export/pdf`, (m) => toast.error(m))}
              data-testid="export-pdf-button"
            >
              <FileDown className="h-4 w-4 mr-1.5" /> PDF
            </Button>
          )}
          {true && (
            <Button
              variant="outline"
              onClick={() => scaricaFile(`${API}/commesse/${id}/export/excel`, (m) => toast.error(m))}
              data-testid="export-excel-button"
            >
              <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Excel
            </Button>
          )}
          {isAdmin && (
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => setConfirmDel(true)}
              title="Elimina commessa"
              data-testid="elimina-commessa-button"
            >
              <Trash2 className="h-4 w-4 mr-1.5" /> {isPrest ? "Elimina prestazione" : "Elimina commessa"}
            </Button>
          )}
        </div>
      </div>

      {true && inPerdita && (
        <div
          className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm font-medium"
          data-testid="perdita-banner"
        >
          <AlertTriangle className="h-5 w-5" />
          Attenzione: questa commessa è in perdita ({euro(data.margine)}).
          {c.alerts?.perdita_inviato && <span className={`ml-auto text-xs font-normal flex items-center gap-1 ${c.alerts.perdita_email?.startsWith("inviata") ? "text-red-500" : "text-red-700"}`} data-testid="alert-email-perdita"><Mail className="h-3.5 w-3.5" /> {c.alerts.perdita_email?.startsWith("inviata") ? `Email ${c.alerts.perdita_email} il ${dateIt(c.alerts.perdita_data)}` : `Email non inviata (${c.alerts.perdita_email || "—"})`}</span>}
        </div>
      )}
      {!isPrest && data.preventivo_superato && !(inPerdita) && (
        <div
          className="flex items-center gap-2 bg-orange-50 border border-orange-300 text-orange-800 rounded-xl px-4 py-3 text-sm font-medium"
          data-testid="preventivo-superato-banner"
        >
          <AlertTriangle className="h-5 w-5" />
          Preventivo superato: i costi reali ({euro(data.costi_reali_totali)}) superano il preventivo ({euro(prev.totale_stimato)}) di {euro(data.scostamento_preventivo)}.
          {true && c.alerts?.preventivo_inviato && <span className="ml-auto text-xs font-normal text-orange-600 flex items-center gap-1" data-testid="alert-email-preventivo"><Mail className="h-3.5 w-3.5" /> {c.alerts.preventivo_email?.startsWith("inviata") ? `Email ${c.alerts.preventivo_email} il ${dateIt(c.alerts.preventivo_data)}` : `Email non inviata (${c.alerts.preventivo_email || "—"})`}</span>}
        </div>
      )}

      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {(isPrest
          ? [
              { label: "Fatturabile totale", value: euro(prestFatt), tone: "text-slate-900", tid: "kpi-fatturato" },
              { label: "Costi Reali", value: euro(data.costi_reali_totali), tone: "text-slate-900", tid: "kpi-costi" },
              { label: "Margine", value: euro(prestMargine), tone: prestMargine < 0 ? "text-red-600" : "text-emerald-600", tid: "kpi-margine" },
              { label: "Margine %", value: `${num(prestFatt ? (prestMargine / prestFatt) * 100 : 0, 1)}%`, tone: prestMargine < 0 ? "text-red-600" : "text-emerald-600", tid: "kpi-margine-perc" },
            ]
          : true
          ? [
              { label: "Fatturato", value: euro(data.importo_commessa), tone: "text-slate-900", tid: "kpi-fatturato" },
              { label: "Costi Reali", value: euro(data.costi_reali_totali), tone: "text-slate-900", tid: "kpi-costi" },
              {
                label: "Margine",
                value: euro(data.margine),
                tone: data.margine < 0 ? "text-red-600" : "text-emerald-600",
                tid: "kpi-margine",
              },
              {
                label: "Margine %",
                value: `${num(data.margine_perc, 1)}%`,
                tone: data.margine < 0 ? "text-red-600" : "text-emerald-600",
                tid: "kpi-margine-perc",
              },
            ]
          : [
              { label: "Costi Reali", value: euro(data.costi_reali_totali), tone: "text-slate-900", tid: "kpi-costi" },
              { label: "Ore Totali", value: `${num(data.ore_totali, 1)} h`, tone: "text-slate-900", tid: "kpi-ore" },
            ]
        ).map((k) => (
          <div key={k.label} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid={k.tid}>
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              {k.label}
            </div>
            <div className={`mt-1.5 font-display text-xl sm:text-2xl font-bold ${k.tone}`}>
              {k.value}
            </div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="dashboard">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">Riepilogo</TabsTrigger>
          <TabsTrigger value="ore" data-testid="tab-ore">Ore ({ore.length})</TabsTrigger>
          <TabsTrigger value="materiali" data-testid="tab-materiali">Acquisti ({materiali.length})</TabsTrigger>
          <TabsTrigger value="trasferte" data-testid="tab-trasferte">Trasferte ({trasferte.length})</TabsTrigger>
          <TabsTrigger value="veicoli" data-testid="tab-veicoli">Veicoli ({veicoliCosti.length})</TabsTrigger>
          {true && <TabsTrigger value="fatture-emesse" data-testid="tab-fatture-emesse">Fatture emesse</TabsTrigger>}
          <TabsTrigger value="magazzino" data-testid="tab-magazzino">Materiali</TabsTrigger>
          <TabsTrigger value="documenti" data-testid="tab-documenti">Documenti</TabsTrigger>
          <TabsTrigger value="diario" data-testid="tab-diario">Diario</TabsTrigger>
          {!isPrest && isAdmin && c.preventivo_origine?.numero && <TabsTrigger value="confronto" data-testid="tab-confronto">Confronto preventivo</TabsTrigger>}
        </TabsList>
        <TabsContent value="confronto" className="mt-4"><ConfrontoPreventivo commessaId={id} /></TabsContent>

        <TabsContent value="magazzino" className="mt-4">
          <MaterialiCommessa commessaId={id} prestazione={isPrest} />
        </TabsContent>

        <TabsContent value="diario" className="mt-4">
          <DiarioCommessa commessaId={id} />
        </TabsContent>

        <TabsContent value="veicoli" className="mt-4">
          <VeicoliCommessa commessaId={id} onChanged={invalidateAll} />
        </TabsContent>
        {true && (
          <TabsContent value="fatture-emesse" className="mt-4">
            <FattureEmesse commessaId={id} importoCommessa={data.importo_commessa} clienteId={data.cliente_id} />
          </TabsContent>
        )}

        <TabsContent value="documenti" className="mt-4">
          <DocumentiCommessa commessaId={id} />
        </TabsContent>

        {/* DASHBOARD */}
        <TabsContent value="dashboard" className="space-y-6 mt-4">
          {isPrest && <RiepilogoPrestazioni commessa={c} />}
          <div className="grid lg:grid-cols-2 gap-6">
            <div className={`bg-white border border-slate-200 rounded-xl p-5 shadow-sm ${isPrest ? "hidden" : ""}`}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-display font-semibold text-slate-800">
                  Preventivo vs Costi Reali
                </h3>
                {true && (
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditPrev(true)} data-testid="modifica-commessa-button">
                    <Pencil className="h-3.5 w-3.5 mr-1" /> Modifica commessa
                  </Button>
                )}
              </div>
              <ModificaCommessa open={editPrev} commessa={c} preventivo={prev} onClose={() => setEditPrev(false)} onSaved={() => { setEditPrev(false); invalidateAll(); }} />
              <div className="divide-y divide-slate-100">
                <CompareRow label="Acquisti (fatture fornitori e materiali)" prev={prev.materiali_stimati} real={data.costo_materiali} testid="cmp-acquisti" />
                {(data.peso_materiali_kg != null || (prev.peso_materiali_stimato_kg || 0) > 0) && (
                  <div className="flex items-center justify-between text-xs text-slate-500 -mt-1 pb-1 border-b border-slate-100" data-testid="materiali-peso-kg">
                    <span className="inline-flex items-center gap-1"><Weight className="h-3 w-3" /> Materiale da magazzino (kg){data.peso_materiali_parziale && <span className="text-amber-600" title="Alcuni articoli non hanno peso unitario">*</span>}
                      {(prev.peso_materiali_stimato_kg || 0) > 0 && data.peso_materiali_kg != null && (
                        <span className={`ml-1 px-1.5 py-0.5 rounded-full font-mono ${data.peso_materiali_kg > prev.peso_materiali_stimato_kg ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`} data-testid="materiali-peso-scostamento">{data.peso_materiali_kg > prev.peso_materiali_stimato_kg ? "+" : ""}{num(data.peso_materiali_kg - prev.peso_materiali_stimato_kg, 1)} kg</span>
                      )}
                    </span>
                    <span className="font-mono">
                      {(prev.peso_materiali_stimato_kg || 0) > 0 && <><span className="text-slate-400">Prev. {num(prev.peso_materiali_stimato_kg, 1)} kg</span> · </>}
                      <span className="font-semibold text-slate-800">Reale {data.peso_materiali_kg != null ? `${num(data.peso_materiali_kg, 1)} kg` : "—"}</span>
                    </span>
                  </div>
                )}
                <CompareRow label="Manodopera ordinaria" prev={prev.ore_costo_stimato} real={data.costo_ore_ordinarie ?? data.costo_ore} testid="cmp-manodopera" />
                <CompareRow label="Manodopera straordinaria" prev={0} real={data.costo_ore_straordinarie || 0} />
                <div className="flex items-center justify-between text-xs text-slate-500 -mt-1 pb-1 border-b border-slate-100" data-testid="manodopera-totale">
                  <span>Manodopera totale ({num(data.ore_totali, 1)} h)</span><span className="font-mono font-semibold text-slate-800">{euro(data.costo_ore)}</span>
                </div>
                <CompareRow label="Trasferte notti (indennità)" prev={prev.trasferta_stimata} real={data.costo_trasferte} testid="cmp-notti" />
                <CompareRow label="Pasto cantiere" prev={prev.pasti_stimati || 0} real={data.costo_pasti || 0} testid="cmp-pasti" />
                <CompareRow label="Cena trasferta" prev={prev.cene_stimate || 0} real={data.costo_cene || 0} testid="cmp-cene" />
                <CompareRow label="Veicoli" prev={prev.veicoli_stimati || 0} real={data.costo_veicoli || 0} testid="cmp-veicoli" />
                {(prev.lavorazioni_esterne_stimate || prev.trattamenti_stimati || prev.trasporti_stimati || prev.costi_comuni_stimati) > 0 && (
                  <div className="pt-3" data-testid="budget-preventivo-extra">
                    <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Altro budget dal preventivo (il reale confluisce in Acquisti)</div>
                    {[["Lavorazioni esterne", prev.lavorazioni_esterne_stimate, "lav-esterne"], ["Trattamenti", prev.trattamenti_stimati, "trattamenti"], ["Trasporti / noleggi", prev.trasporti_stimati, "trasporti"], ["Costi comuni di commessa", prev.costi_comuni_stimati, "costi-comuni"]].filter(([, v]) => v > 0).map(([l, v, t]) => (
                      <div key={t} className="flex justify-between text-sm py-1" data-testid={`budget-${t}`}><span className="text-slate-700">{l}</span><span className="font-mono text-slate-500">Prev. {euro(v)}</span></div>))}
                  </div>
                )}
              </div>
              <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-700">Totale costi</span>
                <div className="text-right font-mono text-sm">
                  <span className="text-slate-400">{euro(prev.totale_stimato)}</span>
                  {" → "}
                  <span
                    className={
                      data.costi_reali_totali > prev.totale_stimato
                        ? "text-red-600 font-bold"
                        : "text-emerald-600 font-bold"
                    }
                  >
                    {euro(data.costi_reali_totali)}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <h3 className="font-display font-semibold text-slate-800 mb-3 flex items-center gap-2">
                <Clock className="h-4 w-4 text-sky-600" /> Ore per dipendente
              </h3>
              {data.ore_per_dipendente.length === 0 ? (
                <p className="text-sm text-slate-400">Nessuna ora registrata.</p>
              ) : (
                <div className="space-y-2">
                  {data.ore_per_dipendente.map((o) => (
                    <div
                      key={o.dipendente_id}
                      className="flex items-center justify-between text-sm border-b border-slate-50 pb-2"
                    >
                      <div>
                        <span className="font-medium text-slate-800">{o.nome}</span>
                        <span className="font-mono text-xs text-slate-400 ml-2">{o.codice}</span>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold">{num(o.ore, 1)} h {o.ore_straordinarie > 0 && <span className="text-xs text-primary font-normal">({num(o.ore_ordinarie, 1)} ord. + {num(o.ore_straordinarie, 1)} str.)</span>}</div>
                        <div className="text-xs text-slate-400 font-mono">{euro(o.costo)}</div>
                      </div>
                    </div>
                  ))}
                  <div className="flex items-center justify-between pt-2 text-sm font-semibold">
                    <span>Totale</span>
                    <span>{num(data.ore_totali, 1)} h · {euro(data.costo_ore)}</span>
                  </div>
                </div>
              )}

              <h3 className="font-display font-semibold text-slate-800 mt-5 mb-3 flex items-center gap-2">
                <BedDouble className="h-4 w-4 text-amber-600" /> Trasferte per dipendente
              </h3>
              {(data.trasferte_per_dipendente || []).length === 0 ? (
                <p className="text-sm text-slate-400">Nessuna notte di trasferta registrata.</p>
              ) : (
                <div className="space-y-2">
                  {data.trasferte_per_dipendente.map((t) => (
                    <div key={t.dipendente_id} className="flex items-center justify-between text-sm border-b border-slate-50 pb-2" data-testid={`trasferta-dip-${t.codice}`}>
                      <span className="font-medium text-slate-800">{t.nome}</span>
                      <span className="text-right"><b>{t.notti}</b> notti · <span className="font-mono">{euro(t.importo)}</span></span>
                    </div>
                  ))}
                </div>
              )}

              <h3 className="font-display font-semibold text-slate-800 mt-5 mb-3 flex items-center gap-2">
                <Car className="h-4 w-4 text-amber-600" /> Costi veicoli
              </h3>
              {Object.keys(data.veicoli_per_tipo || {}).length === 0 ? (
                <p className="text-sm text-slate-400">Nessun costo veicolo registrato.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {Object.entries(data.veicoli_per_tipo).map(([t, v]) => (
                    <div key={t} className="bg-orange-50 border border-orange-100 rounded-lg px-3 py-1.5 text-sm" data-testid={`veicoli-tipo-${t}`}>
                      <span className="text-primary">{VEICOLO_TIPI[t] || t}: </span>
                      <span className="font-semibold font-mono">{euro(v)}</span>
                    </div>
                  ))}
                </div>
              )}
              {Object.keys(data.trasferte_per_tipo).filter((t) => t !== "notti").length > 0 && (
                <>
                  <h3 className="font-display font-semibold text-slate-800 mt-5 mb-3 text-sm text-slate-500">Altre spese trasferta (storico)</h3>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(data.trasferte_per_tipo).filter(([t]) => t !== "notti").map(([t, v]) => (
                      <div key={t} className="bg-amber-50 border border-amber-100 rounded-lg px-3 py-1.5 text-sm">
                        <span className="text-amber-700">{TRASFERTE_TIPI[t] || t}: </span>
                        <span className="font-semibold font-mono">{euro(v)}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </TabsContent>

        {/* ORE */}
        <TabsContent value="ore" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4">
            <RiepilogoOre
              ore={oreFiltrate}
              rimborsi={rimborsi.filter((r) => (filtroDip === "tutti" || r.dipendente_id === filtroDip) && (!oreDal || (r.data || "").slice(0, 10) >= oreDal) && (!oreAl || (r.data || "").slice(0, 10) <= oreAl))}
              trasferte={trasferte.filter((t) => (filtroDip === "tutti" || t.dipendente_id === filtroDip) && (!oreDal || (t.data || "").slice(0, 10) >= oreDal) && (!oreAl || (t.data || "").slice(0, 10) <= oreAl))}
              titolo={filtriOreAttivi ? `Riepilogo filtrato · ${filtroDip === "tutti" ? "tutti i dipendenti" : ore.find((o) => o.dipendente_id === filtroDip)?.dipendente_nome || "—"}${oreDal || oreAl ? ` · ${oreDal ? dateIt(oreDal) : "…"} → ${oreAl ? dateIt(oreAl) : "…"}` : ""}` : "Riepilogo commessa · tutti i dipendenti"}
            />
            <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2" data-testid="ore-inserimento-info">L'inserimento delle ore avviene esclusivamente dal modulo <b>Ore di Lavoro</b>. Qui trovi riepilogo, elenco con filtri, totali ed export.</p>
            <div className="rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 flex items-center justify-between flex-wrap gap-2" data-testid="ore-totale-box">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{!filtriOreAttivi ? "Totale ore" : "Totale ore filtrate"}</div>
                <div className="text-xs text-slate-500 mt-0.5" data-testid="ore-totale-descr">
                  {filtroDip === "tutti" ? "Tutti i dipendenti" : ore.find((o) => o.dipendente_id === filtroDip)?.dipendente_nome || "—"}
                  {(oreDal || oreAl) && ` · Periodo: ${oreDal ? dateIt(oreDal) : "…"} → ${oreAl ? dateIt(oreAl) : "…"}`}
                  {` · ${oreFiltrate.length} righe`}
                  {" · "}ord. {num(oreFiltrate.filter((o) => o.tipo !== "straordinario").reduce((s, o) => s + o.ore, 0), 1)} h · straord. {num(oreFiltrate.filter((o) => o.tipo === "straordinario").reduce((s, o) => s + o.ore, 0), 1)} h
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="font-mono text-2xl font-semibold text-slate-900" data-testid="ore-totale-ore">{num(oreFiltrate.reduce((s, o) => s + o.ore, 0), 1)} h</div>
                <div className="font-mono text-2xl font-semibold text-primary" data-testid="ore-totale-costo">{euro(oreFiltrate.reduce((s, o) => s + o.costo, 0))}</div>
                {true && (
                  <Button size="sm" variant="outline" className="h-8" disabled={oreFiltrate.length === 0} onClick={() => {
                    const p = new URLSearchParams();
                    if (filtroDip !== "tutti") p.set("dipendente_id", filtroDip);
                    if (oreDal) p.set("dal", oreDal);
                    if (oreAl) p.set("al", oreAl);
                    scaricaFile(`${API}/commesse/${id}/ore/export/excel?${p.toString()}`, (m) => toast.error(m));
                  }} data-testid="ore-export-excel">
                    <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Excel {filtriOreAttivi ? "filtrato" : ""}
                  </Button>
                )}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3 pt-1" data-testid="ore-filtro-bar">
              <Label className="text-xs text-slate-500">Filtra per dipendente</Label>
              <Select value={filtroDip} onValueChange={setFiltroDip}>
                <SelectTrigger className="h-8 w-[240px]" data-testid="ore-filtro-dipendente"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tutti">Tutti i dipendenti</SelectItem>
                  {[...new Map(ore.map((o) => [o.dipendente_id, o.dipendente_nome])).entries()].map(([did, nome]) => (
                    <SelectItem key={did} value={did}>{nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Label className="text-xs text-slate-500 ml-2">Dal</Label>
              <Input type="date" className="h-8 w-[150px]" value={oreDal} onChange={(e) => setOreDal(e.target.value)} data-testid="ore-filtro-dal" />
              <Label className="text-xs text-slate-500">Al</Label>
              <Input type="date" className="h-8 w-[150px]" value={oreAl} onChange={(e) => setOreAl(e.target.value)} data-testid="ore-filtro-al" />
              {filtriOreAttivi && (
                <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => { setFiltroDip("tutti"); setOreDal(""); setOreAl(""); }} data-testid="ore-filtro-reset">Azzera filtri</Button>
              )}
            </div>
            <DataTable
              rows={oreFiltrate}
              cols={[
                { k: "data", label: "Data", render: (r) => dateIt(r.data) },
                { k: "dipendente_nome", label: "Dipendente" },
                { k: "ore", label: "Ore", render: (r) => `${num(r.ore, 1)} h` },
                { k: "tipo", label: "Tipo", render: (r) => (r.tipo && r.tipo !== "ordinario" ? <span className="text-xs font-semibold text-primary">{TIPO_ORA_LABEL[r.tipo] || r.tipo}</span> : <span className="text-xs text-slate-500">Ord.</span>) },
                { k: "luogo", label: "Luogo", render: (r) => (r.luogo === "cantiere" ? <span className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">Cantiere</span> : r.luogo === "officina" ? <span className="text-xs text-slate-600 bg-slate-100 rounded-full px-2 py-0.5">Officina</span> : <span className="text-xs text-slate-300">—</span>) },
                { k: "costo_orario", label: "€/h", render: (r) => euro(r.costo_orario) },
                { k: "costo", label: "Costo", render: (r) => euro(r.costo), right: true },
              ]}
              onDelete={(r) => delMut.mutate({ kind: "ore", itemId: r.id, qkey: "ore" })}
              onDeleteMany={(righe) => eliminaMolte("ore", "ore", righe)}
              onEdit={(r) => setOraInModifica(r)}
              onEditMany={(righe, reset) => setOreBulk({ righe, reset })}
              testidPrefix="ore-row"
            />
            <ModificaOreBulk
              righe={oreBulk?.righe || null}
              onClose={() => setOreBulk(null)}
              onSaved={() => { oreBulk?.reset?.(); setOreBulk(null); qc.invalidateQueries({ queryKey: ["ore", id] }); qc.invalidateQueries({ queryKey: ["rimborsi", id] }); invalidateAll(); }}
            />
            <ModificaOra
              riga={oraInModifica}
              dipendenti={dipendenti}
              onClose={() => setOraInModifica(null)}
              onSaved={() => {
                setOraInModifica(null);
                qc.invalidateQueries({ queryKey: ["ore", id] });
                invalidateAll();
              }}
            />
            <div className="pt-2 border-t border-slate-100" data-testid="rimborsi-section">
              <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-2">
                <UtensilsCrossed className="h-4 w-4 text-amber-600" /> Pasto cantiere / Cena trasferta ({rimborsi.length})
                {rimbDupIds.size > 0 && <button onClick={() => setRimbSoloDup((v) => !v)} className={`ml-2 inline-flex items-center gap-1 text-xs font-medium rounded-full px-2 py-0.5 border ${rimbSoloDup ? "bg-amber-600 text-white border-amber-600" : "bg-amber-50 text-amber-800 border-amber-200"}`} data-testid="rimb-controlla-duplicati"><AlertTriangle className="h-3 w-3" /> {rimbSoloDup ? "Mostra tutti" : `Controlla duplicati (${rimbDupIds.size})`}</button>}
              </h3>
              {rimborsi.length === 0 ? (
                <p className="text-xs text-slate-400" data-testid="rimborsi-empty">Nessun rimborso registrato.</p>
              ) : (
                <div className="space-y-1.5">
                  {rimborsi.filter((r) => !rimbSoloDup || rimbDupIds.has(r.id)).map((r) => (
                    <div key={r.id} className={`flex items-center justify-between text-sm border rounded-lg px-3 py-2 ${rimbDupIds.has(r.id) ? "bg-amber-100 border-amber-400" : "bg-amber-50/60 border-amber-100"}`} data-testid={`rimborso-row-${r.id}`}>
                      <span className="text-slate-700">{dateIt(r.data)} · {r.dipendente_nome} · {r.descrizione}{rimbDupIds.has(r.id) && <span className="ml-2 text-[10px] uppercase tracking-wider text-amber-800 font-semibold" data-testid={`rimborso-dup-${r.id}`}>duplicato</span>}</span>
                      <span className="flex items-center gap-3">
                        <span className="font-mono font-medium">{euro(r.importo)}</span>
                        {TRASFERTE_MODIFICABILI && (
                          <button onClick={() => rimbDelMut.mutate(r.id)} className="text-slate-300 hover:text-red-600" title="Elimina rimborso" data-testid={`rimborso-delete-${r.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* FATTURE FORNITORI */}
        <TabsContent value="materiali" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm text-slate-500">Fatture di acquisto per servizi (noleggi, subappalti, manodopera specializzata…) scelti dal catalogo Servizi dell'Anagrafica. I DDT di materiale si registrano dal Magazzino.</div>
              <Button onClick={() => setCaricoOpen(true)} data-testid="mat-nuovo-documento"><Plus className="h-4 w-4 mr-1.5" /> Nuova fattura di acquisto</Button>
            </div>
            <Dialog open={caricoOpen} onOpenChange={setCaricoOpen}>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="fattura-servizi-dialog">
                <DialogHeader><DialogTitle className="font-display">Nuova fattura di acquisto</DialogTitle><DialogDescription>Fornitore, numero fattura e voci scelte dal catalogo Servizi. L'importo entra nei costi di questa {isPrest ? "prestazione" : "commessa"}.</DialogDescription></DialogHeader>
                <AcquistiForm form={matForm} setForm={setMatForm} saving={matMut.isPending} onSubmit={(payloads) => matMut.mutate(payloads, { onSuccess: () => setCaricoOpen(false) })} />
              </DialogContent>
            </Dialog>
            {caricoInModifica && <CaricoDialog open onClose={() => setCaricoInModifica(null)} commessaFissa={id} prestazione={isPrest} editId={caricoInModifica.editId} iniziale={caricoInModifica.iniziale} onSaved={() => { qc.invalidateQueries({ queryKey: ["materiali", id] }); invalidateAll(); }} />}
            <div className="rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 flex items-center justify-between flex-wrap gap-2" data-testid="mat-totale-box">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  {!filtriMatAttivi ? "Totale acquisti" : "Totale acquisti filtrati"}
                </div>
                <div className="text-xs text-slate-500 mt-0.5" data-testid="mat-totale-descr">
                  {!filtriMatAttivi
                    ? `Tutte le ${materiali.length} voci della commessa`
                    : [
                        filtroForn !== "tutti" && `Fornitore: ${materiali.find((m) => (m.fornitore_id || "__nessuno__") === filtroForn)?.fornitore_nome || "—"}`,
                        filtroMat !== "tutti" && `Materiale: ${materialiFiltrati[0]?.descrizione || filtroMat}`,
                        filtroPag !== "tutti" && (filtroPag === "pagate" ? "Solo pagate" : "Solo da pagare"),
                        (filtroDal || filtroAl) && `Periodo: ${filtroDal ? dateIt(filtroDal) : "…"} → ${filtroAl ? dateIt(filtroAl) : "…"}`,
                      ].filter(Boolean).join(" · ") + ` · ${materialiFiltrati.length} voci`}
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <div className="font-mono text-2xl font-semibold text-primary" data-testid="mat-totale-valore">{euro(materialiFiltrati.reduce((s, m) => s + (m.costo_totale || 0), 0))}</div>
                  {true && filtroPag === "tutti" && (
                    <div className="text-xs text-amber-700" data-testid="mat-totale-da-pagare">
                      da saldare {euro(materialiFiltrati.filter((m) => !m.data_pagamento).reduce((s, m) => s + (m.costo_totale || 0), 0))}
                    </div>
                  )}
                </div>
                {true && (
                  <Button size="sm" variant="outline" className="h-8" disabled={materialiFiltrati.length === 0} onClick={() => {
                    const p = new URLSearchParams();
                    if (filtroForn !== "tutti") p.set("fornitore_id", filtroForn);
                    if (filtroMat !== "tutti") p.set("materiale", materialiFiltrati[0]?.descrizione?.trim() || filtroMat);
                    if (filtroPag !== "tutti") p.set("pagamento", filtroPag);
                    if (filtroDal) p.set("dal", filtroDal);
                    if (filtroAl) p.set("al", filtroAl);
                    scaricaFile(`${API}/commesse/${id}/acquisti/export/excel?${p.toString()}`, (m) => toast.error(m));
                  }} data-testid="mat-export-excel">
                    <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Excel {filtriMatAttivi ? "filtrato" : ""}
                  </Button>
                )}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3" data-testid="mat-filtro-bar">
              <Label className="text-xs text-slate-500">Filtra per fornitore</Label>
              <Select value={filtroForn} onValueChange={setFiltroForn}>
                <SelectTrigger className="h-8 w-[240px]" data-testid="mat-filtro-fornitore"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tutti">Tutti i fornitori</SelectItem>
                  {[...new Map(materiali.map((m) => [m.fornitore_id || "__nessuno__", m.fornitore_nome])).entries()].map(([fid, nome]) => (
                    <SelectItem key={fid} value={fid}>{nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Label className="text-xs text-slate-500 ml-2">Filtra per materiale</Label>
              <Select value={filtroMat} onValueChange={setFiltroMat}>
                <SelectTrigger className="h-8 w-[260px]" data-testid="mat-filtro-materiale"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tutti">Tutti i materiali</SelectItem>
                  {[...new Map(materiali.filter((m) => (m.descrizione || "").trim()).map((m) => [m.descrizione.trim().toLowerCase(), m.descrizione.trim()])).entries()]
                    .sort((a, b) => a[1].localeCompare(b[1]))
                    .map(([k, nome]) => <SelectItem key={k} value={k}>{nome}</SelectItem>)}
                </SelectContent>
              </Select>
              <Label className="text-xs text-slate-500 ml-2">Pagamento</Label>
              <Select value={filtroPag} onValueChange={setFiltroPag}>
                <SelectTrigger className="h-8 w-[150px]" data-testid="mat-filtro-pagamento"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tutti">Tutte</SelectItem>
                  <SelectItem value="pagate">Pagate</SelectItem>
                  <SelectItem value="da_pagare">Da pagare</SelectItem>
                </SelectContent>
              </Select>
              <Label className="text-xs text-slate-500 ml-2">Dal</Label>
              <Input type="date" className="h-8 w-[150px]" value={filtroDal} onChange={(e) => setFiltroDal(e.target.value)} data-testid="mat-filtro-dal" />
              <Label className="text-xs text-slate-500">Al</Label>
              <Input type="date" className="h-8 w-[150px]" value={filtroAl} onChange={(e) => setFiltroAl(e.target.value)} data-testid="mat-filtro-al" />
              {filtriMatAttivi && (
                <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => { setFiltroForn("tutti"); setFiltroMat("tutti"); setFiltroPag("tutti"); setFiltroDal(""); setFiltroAl(""); }} data-testid="mat-filtro-reset">Azzera filtri</Button>
              )}
            </div>
            <DataTable
              rows={materialiFiltrati}
              cols={[
                { k: "data", label: "Data", render: (r) => dateIt(r.data) },
                { k: "fornitore_nome", label: "Fornitore" },
                { k: "numero_fattura", label: "N. documento", render: (r) => r.ddt || r.numero_fattura || "—" },
                { k: "descrizione", label: "Materiale" },
                { k: "quantita", label: "Q.tà", render: (r) => (r.quantita ? `${num(r.quantita, 2)} ${r.unita || ""}` : <span className="text-slate-300">—</span>) },
                { k: "costo_unitario", label: "€/unità", render: (r) => (r.costo_unitario != null && r.quantita ? euro(r.costo_unitario) : <span className="text-slate-300">—</span>) },
                { k: "costo_totale", label: "Importo", render: (r) => euro(r.costo_totale), right: true },
                {
                  k: "data_pagamento",
                  label: "Pagamento",
                  render: (r) =>
                    r.data_pagamento ? (
                      <span className="text-emerald-700 text-xs font-medium inline-flex items-center gap-1">
                        Pagata {dateIt(r.data_pagamento)}
                        {true && <button onClick={() => pagMut.mutate({ id: r.id, data_pagamento: null })} className="text-slate-300 hover:text-slate-600" title="Annulla" data-testid={`mat-annulla-pagamento-${r.id}`}>×</button>}
                      </span>
                    ) : true ? (
                      <Button size="sm" variant="outline" className="h-7 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => pagMut.mutate({ id: r.id, data_pagamento: new Date().toISOString().slice(0, 10) })} data-testid={`mat-paga-${r.id}`}>Segna pagata</Button>
                    ) : (
                      <span className="text-xs text-amber-700">Da pagare</span>
                    ),
                },
              ]}
              onDelete={isAdmin ? (r) => delMut.mutate({ kind: "materiali", itemId: r.id, qkey: "materiali" }) : undefined}
              onDeleteMany={isAdmin ? (righe) => eliminaMolte("materiali", "materiali", righe) : undefined}
              onEdit={(r) => apriModificaAcquisto(r)}
              onEditMany={(righe, reset) => setMatBulk({ righe, reset })}
              groupBy={chiaveFattura}
              groupHeader={(righe) => {
                const m = righe[0];
                const tot = righe.reduce((s, r) => s + (r.costo_totale || 0), 0);
                return (
                  <span className="flex items-center gap-2 flex-wrap">
                    <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-primary border border-orange-200" data-testid={`doc-badge-${m.id}`}>{m.tipo_documento === "ddt" || (m.ddt && !m.numero_fattura) ? `DDT ${m.ddt || "s/n"}` : `Fattura ${m.numero_fattura || "s/n"}`}</span>
                    <span className="font-medium text-slate-800">{m.fornitore_nome}</span>
                    {isPrest && <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold border ${m.fatturazione === "costo_interno" ? "bg-slate-100 text-slate-700 border-slate-200" : m.fatturazione === "pagato_anticipato" ? "bg-sky-50 text-sky-700 border-sky-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`} data-testid={`tipo-spesa-${m.id}`}>{m.fatturazione === "costo_interno" ? "Costo interno" : m.fatturazione === "pagato_anticipato" ? "Già pagato dal cliente" : "Materiale fatturabile"}</span>}
                    <span className="text-slate-400">· {dateIt(m.data)} · {righe.length} voc{righe.length === 1 ? "e" : "i"}</span>
                    {true && (
                      <span className="flex items-center gap-1.5 ml-2">
                        {righe.every((r) => r.data_pagamento) ? (
                          <span className="inline-flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" data-testid={`fattura-pagata-${m.id}`}>
                            Pagata {dateIt(righe[0].data_pagamento)}
                            <button onClick={() => pagFatturaMut.mutate({ ids: righe.map((r) => r.id), data_pagamento: null })} className="text-emerald-400 hover:text-red-600" title="Annulla pagamento" data-testid={`fattura-annulla-pagamento-${m.id}`}>×</button>
                          </span>
                        ) : (
                          <Button size="sm" variant="outline" className="h-6 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => pagFatturaMut.mutate({ ids: righe.map((r) => r.id), data_pagamento: new Date().toISOString().slice(0, 10) })} data-testid={`fattura-paga-${m.id}`}>
                            Segna fattura pagata
                          </Button>
                        )}
                        <button onClick={() => (m.mag_carico_id ? apriModificaAcquisto(m) : setTestataInModifica(righe))} className="text-slate-400 hover:text-primary" title="Modifica documento" data-testid={`fattura-modifica-testata-${m.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                      </span>
                    )}
                    <span className="ml-auto font-mono font-semibold text-slate-900">{euro(tot)}</span>
                  </span>
                );
              }}
              testidPrefix="mat-row"
              emptyIcon={FileText}
            />
            <ModificaAcquistiBulk
              isAdmin={true}
              righe={matBulk?.righe || null}
              onClose={() => setMatBulk(null)}
              onSaved={() => { matBulk?.reset?.(); setMatBulk(null); qc.invalidateQueries({ queryKey: ["materiali", id] }); invalidateAll(); }}
            />
            <ModificaTestataFattura
              righe={testataInModifica}
              onClose={() => setTestataInModifica(null)}
              onSaved={() => { setTestataInModifica(null); qc.invalidateQueries({ queryKey: ["materiali", id] }); invalidateAll(); }}
            />
            <ModificaFattura
              riga={fatturaInModifica}
              onClose={() => setFatturaInModifica(null)}
              onSaved={() => { setFatturaInModifica(null); qc.invalidateQueries({ queryKey: ["materiali", id] }); invalidateAll(); }}
            />
          </div>
        </TabsContent>

        {/* TRASFERTE */}
        <TabsContent value="trasferte" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4">
            <div className="rounded-lg border border-sky-200 bg-sky-50 px-4 py-2.5 text-sm text-sky-900 flex items-center gap-2" data-testid="tra-sola-lettura">
              <Lock className="h-4 w-4 shrink-0" /> Le trasferte sono in sola lettura: qualsiasi correzione (notti, cene, pasti, ore) si fa da <button className="underline font-medium" onClick={() => navigate("/ore-rapide")} data-testid="tra-vai-ore">Ore di Lavoro → Storico e modifica registrazioni</button>.
            </div>
            {TRASFERTE_MODIFICABILI && <div className="grid sm:grid-cols-6 gap-3 items-end">
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Dipendenti *</Label>
                <SelettoreDipendenti
                  dipendenti={dipendenti}
                  selezionati={traForm.dipendenti_ids}
                  onChange={(ids) => setTraForm((f) => ({ ...f, dipendenti_ids: ids }))}
                  etichetta={(d) => `${d.nome} ${d.cognome}`}
                  testid="tra-dipendente-select"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Notte del</Label>
                <Input
                  type="date"
                  value={traForm.data}
                  onChange={(e) => setTraForm((f) => ({ ...f, data: e.target.value }))}
                  data-testid="tra-data-input"
                />
                <SelettoreGiorni giorni={traGiorni} onChange={setTraGiorni} testid="tra-giorni" />
                <ConfermaMultiGiorno
                  open={!!pendingTra}
                  onOpenChange={(v) => !v && setPendingTra(null)}
                  onConfirm={() => pendingTra && traMut.mutate(pendingTra)}
                  giorni={pendingTra ? [...pendingTra.giorni].sort().slice(0, pendingTra.giorni.length > 1 ? -1 : undefined) : []}
                  righePerGiorno={pendingTra?.dipendenti_ids?.length || 1}
                  dipendente={dipendenti.filter((d) => pendingTra?.dipendenti_ids?.includes(d.id)).map((d) => `${d.nome} ${d.cognome}`.trim()).join(", ")}
                  loading={traMut.isPending}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Indennità/notte (€)</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={traForm.indennita_notte === "" ? (impostazioni?.indennita_notte ?? "") : traForm.indennita_notte}
                  onChange={(e) => setTraForm((f) => ({ ...f, indennita_notte: e.target.value }))}
                  data-testid="tra-indennita-input"
                />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Descrizione</Label>
                <Input
                  value={traForm.descrizione}
                  onChange={(e) => setTraForm((f) => ({ ...f, descrizione: e.target.value }))}
                  placeholder="es. Cantiere Alfa, settimana 12"
                  data-testid="tra-descrizione-input"
                />
              </div>
              <div className="sm:col-span-5 text-sm text-slate-500" data-testid="tra-anteprima">
                {(() => {
                  const nGsel = traGiorni.length > 0 ? traGiorni.length : 1;
                  const nG = nGsel > 1 ? nGsel - 1 : 1;
                  const nD = traForm.dipendenti_ids.length;
                  const ind = Number(traForm.indennita_notte === "" ? impostazioni?.indennita_notte : traForm.indennita_notte) || 0;
                  return <>{nD} dipendent{nD === 1 ? "e" : "i"} × {nG} nott{nG === 1 ? "e" : "i"}{nGsel > 1 && <span className="text-slate-400"> ({nGsel} giorni − 1)</span>} = <b>{nD * nG}</b> righe · Totale indennità: <b className="text-primary font-mono">{euro(nD * nG * ind)}</b></>;
                })()}
              </div>
              <Button
                onClick={() => {
                  const dips = traForm.dipendenti_ids;
                  if (dips.length === 0) return toast.error("Seleziona almeno un dipendente");
                  const giorni = traGiorni.length > 0 ? traGiorni : [traForm.data || new Date().toISOString().slice(0, 10)];
                  const gs = [...new Set(giorni.map((g) => g.slice(0, 10)))].sort();
                  const notti = gs.length > 1 ? gs.slice(0, -1) : gs;
                  const dup = [];
                  for (const did of dips) for (const g of notti) {
                    if (trasferte.some((t) => t.dipendente_id === did && (t.data || "").slice(0, 10) === g)) { const d = dipendenti.find((x) => x.id === did); dup.push(`${d ? `${d.nome} ${d.cognome}`.trim() : "dipendente"} in data ${dateIt(g)}`); }
                  }
                  if (dup.length && !window.confirm(`Attenzione: esiste già una trasferta per ${dup.join(", ")}. Vuoi aggiungerla comunque?`)) return;
                  const payload = {
                    dipendenti_ids: dips,
                    giorni,
                    indennita_notte: traForm.indennita_notte === "" ? undefined : Number(traForm.indennita_notte),
                    descrizione: traForm.descrizione,
                  };
                  if (giorni.length > 1 || dips.length > 1) setPendingTra(payload);
                  else traMut.mutate(payload);
                }}
                disabled={traMut.isPending}
                data-testid="tra-add-button"
              >
                <Plus className="h-4 w-4 mr-1" /> Aggiungi
              </Button>
            </div>}
            {TRASFERTE_MODIFICABILI && <p className="text-xs text-slate-400 -mt-1">{traGiorni.length > 1 && <span className="text-primary mr-1">Regola notti = giorni − 1: con {traGiorni.length} giorni selezionati verranno create {traGiorni.length - 1} notti per ogni dipendente (l'ultimo giorno è il rientro).</span>}L'indennità predefinita si imposta in Anagrafica → Dipendenti. Il totale finisce nei costi della commessa e in Ore Mensili come "Indennità trasferta" per la busta paga.</p>}
            <div className="rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 flex items-center justify-between flex-wrap gap-2" data-testid="tra-totale-box">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{filtriTraAttivi ? "Totale trasferte filtrato" : "Totale trasferte"}</div>
                <div className="text-xs text-slate-500 mt-0.5" data-testid="tra-totale-descr">
                  {[
                    traDip !== "tutti" ? trasferte.find((t) => t.dipendente_id === traDip)?.dipendente_nome || "—" : "Tutti i dipendenti",
                    (traDal || traAl) && `Periodo: ${traDal ? dateIt(traDal) : "…"} → ${traAl ? dateIt(traAl) : "…"}`,
                    `${trasferteFiltrate.length} righe`,
                    `${trasferteFiltrate.reduce((s, t) => s + (t.notti || 0), 0)} notti`,
                  ].filter(Boolean).join(" · ")}
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="font-mono text-2xl font-semibold text-primary" data-testid="tra-totale-valore">{euro(trasferteFiltrate.reduce((s, t) => s + (t.importo || 0), 0))}</div>
                {true && (
                  <Button size="sm" variant="outline" className="h-8" disabled={trasferteFiltrate.length === 0} onClick={() => {
                    const p = new URLSearchParams();
                    if (traDip !== "tutti") p.set("dipendente_id", traDip);
                    if (traDal) p.set("dal", traDal);
                    if (traAl) p.set("al", traAl);
                    scaricaFile(`${API}/commesse/${id}/trasferte/export/excel?${p.toString()}`, (m) => toast.error(m));
                  }} data-testid="tra-export-excel">
                    <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Excel {filtriTraAttivi ? "filtrato" : ""}
                  </Button>
                )}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3" data-testid="tra-filtro-bar">
              <Label className="text-xs text-slate-500">Filtra per dipendente</Label>
              <Select value={traDip} onValueChange={setTraDip}>
                <SelectTrigger className="h-8 w-[240px]" data-testid="tra-filtro-dipendente"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tutti">Tutti i dipendenti</SelectItem>
                  {[...new Map(trasferte.map((t) => [t.dipendente_id, t.dipendente_nome])).entries()].map(([did, nome]) => <SelectItem key={did} value={did}>{nome}</SelectItem>)}
                </SelectContent>
              </Select>
              <Label className="text-xs text-slate-500 ml-2">Dal</Label>
              <Input type="date" className="h-8 w-[150px]" value={traDal} onChange={(e) => setTraDal(e.target.value)} data-testid="tra-filtro-dal" />
              <Label className="text-xs text-slate-500">Al</Label>
              <Input type="date" className="h-8 w-[150px]" value={traAl} onChange={(e) => setTraAl(e.target.value)} data-testid="tra-filtro-al" />
              <Button size="sm" variant={traSoloDup ? "default" : "outline"} className="h-8 text-xs ml-2" onClick={() => setTraSoloDup((v) => !v)} data-testid="tra-controlla-duplicati">
                <AlertTriangle className="h-3.5 w-3.5 mr-1" /> {traSoloDup ? `Duplicati: ${traDupIds.size} righe` : `Controlla duplicati${traDupIds.size ? ` (${traDupIds.size})` : ""}`}
              </Button>
              {traSoloDup && TRASFERTE_MODIFICABILI && isAdmin && traDupIds.size > 0 && <Button size="sm" variant="outline" className="h-8 text-xs text-red-700 border-red-200" onClick={async () => { if (!window.confirm("Per ogni coppia duplicata verrà eliminata la copia più recente (e la cena collegata), tenendo la più vecchia. Procedere?")) return; try { const d = (await api.post(`/commesse/${id}/trasferte/elimina-duplicati`)).data; toast.success(`${d.eliminate} duplicati eliminati`); qc.invalidateQueries({ queryKey: ["trasferte", id] }); qc.invalidateQueries({ queryKey: ["rimborsi", id] }); invalidateAll(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } }} data-testid="tra-elimina-duplicati"><Trash2 className="h-3.5 w-3.5 mr-1" /> Elimina copie più recenti</Button>}
              {traSoloDup && <span className="text-xs text-amber-700" data-testid="tra-duplicati-info">{traDupIds.size ? "Righe con stesso dipendente e stessa data: elimina quelle sbagliate." : "Nessun duplicato trovato."}</span>}
              {filtriTraAttivi && (
                <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => { setTraDip("tutti"); setTraDal(""); setTraAl(""); setTraSoloDup(false); }} data-testid="tra-filtro-reset">Azzera filtri</Button>
              )}
            </div>
            <DataTable
              rows={trasferteFiltrate}
              cols={[
                { k: "data", label: "Data", render: (r) => dateIt(r.data) },
                { k: "dipendente_nome", label: "Dipendente", render: (r) => r.dipendente_nome || "—" },
                { k: "notti", label: "Notti", render: (r) => (r.notti != null ? r.notti : TRASFERTE_TIPI[r.tipo] || r.tipo) },
                { k: "indennita_notte", label: "€/notte", render: (r) => (r.indennita_notte != null ? euro(r.indennita_notte) : "—") },
                { k: "descrizione", label: "Descrizione" },
                { k: "importo", label: "Importo", render: (r) => euro(r.importo), right: true },
              ]}
              onDelete={TRASFERTE_MODIFICABILI ? (r) => delMut.mutate({ kind: "trasferte", itemId: r.id, qkey: "trasferte" }) : undefined}
              onDeleteMany={TRASFERTE_MODIFICABILI ? (righe) => eliminaMolte("trasferte", "trasferte", righe) : undefined}
              onEdit={TRASFERTE_MODIFICABILI ? (r) => setTrasfertaInModifica(r) : undefined}
              onEditMany={TRASFERTE_MODIFICABILI ? (righe, reset) => setTraBulk({ righe, reset }) : undefined}
              testidPrefix="tra-row"
              emptyIcon={BedDouble}
            />
            <ModificaTrasferteBulk
              righe={traBulk?.righe || null}
              onClose={() => setTraBulk(null)}
              onSaved={() => { traBulk?.reset?.(); setTraBulk(null); qc.invalidateQueries({ queryKey: ["trasferte", id] }); qc.invalidateQueries({ queryKey: ["rimborsi", id] }); invalidateAll(); }}
            />
            <ModificaTrasferta
              riga={trasfertaInModifica}
              dipendenti={dipendenti}
              onClose={() => setTrasfertaInModifica(null)}
              onSaved={() => { setTrasfertaInModifica(null); qc.invalidateQueries({ queryKey: ["trasferte", id] }); qc.invalidateQueries({ queryKey: ["rimborsi", id] }); invalidateAll(); }}
            />
          </div>
        </TabsContent>
      </Tabs>
      <ConfermaEliminaCommessa
        open={confirmDel}
        onOpenChange={(v) => { setConfirmDel(v); if (!v) setBloccoFatture(null); }}
        onConfirm={(force) => delCommessaMut.mutate(force)}
        loading={delCommessaMut.isPending}
        bloccoFatture={bloccoFatture}
      />
    </div>
  );
}

const TRASFERTE_MODIFICABILI = false; // unica fonte: Ore di Lavoro → Storico e modifica registrazioni

function DataTable({ rows, cols, onDelete, onDeleteMany, onEdit, onEditMany, testidPrefix, emptyIcon: EmptyIcon = Clock, groupBy, groupHeader }) {
  const [selezionate, setSelezionate] = useState([]);
  const [conferma, setConferma] = useState(false);
  const [eliminando, setEliminando] = useState(false);
  const ids = rows.map((r) => r.id);
  const sel = selezionate.filter((s) => ids.includes(s));
  const tutte = rows.length > 0 && sel.length === rows.length;
  const toggle = (rid) => setSelezionate((s) => (s.includes(rid) ? s.filter((x) => x !== rid) : [...s, rid]));
  const eliminaSelezionate = async () => {
    setEliminando(true);
    try {
      await onDeleteMany(rows.filter((r) => sel.includes(r.id)));
      setSelezionate([]);
      setConferma(false);
    } finally {
      setEliminando(false);
    }
  };
  if (rows.length === 0)
    return (
      <div className="text-center py-10 text-slate-400 border border-dashed border-slate-200 rounded-lg">
        <EmptyIcon className="h-8 w-8 mx-auto mb-2 opacity-40" />
        Nessuna voce registrata.
      </div>
    );
  const conSelezione = Boolean(onDeleteMany || onEditMany);
  return (
    <div className="space-y-2">
      {conSelezione && sel.length > 0 && (
        <div className="flex items-center justify-between flex-wrap gap-2 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-sm" data-testid={`${testidPrefix}-bulk-bar`}>
          <span><b>{sel.length}</b> righe selezionate</span>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" className="h-8" onClick={() => setSelezionate([])} data-testid={`${testidPrefix}-bulk-deseleziona`}>Deseleziona</Button>
            {onEditMany && (
              <Button size="sm" className="h-8" onClick={() => onEditMany(rows.filter((r) => sel.includes(r.id)), () => setSelezionate([]))} data-testid={`${testidPrefix}-bulk-modifica`}>
                <Pencil className="h-4 w-4 mr-1.5" /> Modifica selezionati
              </Button>
            )}
            {onDeleteMany && (
            <Button size="sm" className="h-8 bg-red-600 hover:bg-red-700 text-white" onClick={() => setConferma(true)} data-testid={`${testidPrefix}-bulk-elimina`}>
              <Trash2 className="h-4 w-4 mr-1.5" /> Elimina selezionati
            </Button>
            )}
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
            {conSelezione && (
              <th className="py-2 px-3 w-8">
                <Checkbox checked={tutte} onCheckedChange={(v) => setSelezionate(v ? ids : [])} aria-label="Seleziona tutto" data-testid={`${testidPrefix}-seleziona-tutto`} />
              </th>
            )}
            {cols.map((c) => (
              <th key={c.k} className={`py-2 px-3 ${c.right ? "text-right" : ""}`}>
                {c.label}
              </th>
            ))}
            <th className="py-2 px-3"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => (
            <React.Fragment key={r.id}>
            {groupBy && (idx === 0 || groupBy(rows[idx - 1]) !== groupBy(r)) && (
              <tr className="bg-slate-50/80 border-t border-slate-200" data-testid={`${testidPrefix}-gruppo-${r.id}`}>
                <td colSpan={cols.length + 1 + (conSelezione ? 1 : 0)} className="py-1.5 px-3 text-sm">
                  {groupHeader(rows.filter((x) => groupBy(x) === groupBy(r)))}
                </td>
              </tr>
            )}
            <tr className={`border-b border-slate-50 ${sel.includes(r.id) ? "bg-orange-50/60" : r._dup ? "bg-amber-50 ring-1 ring-inset ring-amber-300" : ""}`} data-testid={`${testidPrefix}-${r.id}`}>
              {conSelezione && (
                <td className="py-2.5 px-3">
                  <Checkbox checked={sel.includes(r.id)} onCheckedChange={() => toggle(r.id)} data-testid={`${testidPrefix}-check-${r.id}`} />
                </td>
              )}
              {cols.map((c) => (
                <td
                  key={c.k}
                  className={`py-2.5 px-3 ${c.right ? "text-right font-mono font-medium" : "text-slate-700"}`}
                >
                  {c.render ? c.render(r) : r[c.k]}
                </td>
              ))}
              <td className="py-2.5 px-3 text-right whitespace-nowrap">
                {onEdit && (
                  <button
                    onClick={() => onEdit(r)}
                    className="text-slate-300 hover:text-primary transition-colors mr-3"
                    title="Modifica"
                    data-testid={`edit-${testidPrefix}-${r.id}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                )}
                {onDelete && (
                <button
                  onClick={() => onDelete(r)}
                  className="text-slate-300 hover:text-red-600 transition-colors"
                  data-testid={`delete-${testidPrefix}-${r.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
                )}
              </td>
            </tr>
            </React.Fragment>
          ))}
        </tbody>
      </table>
      </div>
      {onDeleteMany && (
        <AlertDialog open={conferma} onOpenChange={setConferma}>
          <AlertDialogContent data-testid={`${testidPrefix}-bulk-conferma`}>
            <AlertDialogHeader>
              <AlertDialogTitle className="font-display">Elimina {sel.length} righe</AlertDialogTitle>
              <AlertDialogDescription>Le {sel.length} righe selezionate verranno eliminate definitivamente. L'operazione non può essere annullata.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid={`${testidPrefix}-bulk-annulla`}>Annulla</AlertDialogCancel>
              <AlertDialogAction className="bg-red-600 hover:bg-red-700 text-white" disabled={eliminando} onClick={(e) => { e.preventDefault(); eliminaSelezionate(); }} data-testid={`${testidPrefix}-bulk-conferma-ok`}>
                {eliminando ? "Eliminazione…" : "Elimina"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}
